import moment from 'moment'

import { InventoryComparisonRow } from '../../types/inventoryHealth'
import { ComLog } from '../../types/monitoring'

/**
 * Everything on the comparison table is STORED state. This answers the question
 * the stored state cannot: did the PMS actually answer us, and did that answer
 * reach our inventory?
 *
 * The check is deliberately not "parse the provider's response and compare the
 * numbers". Every provider has its own payload format, so that is a parser per
 * provider and a permanent maintenance burden. pmsmanager ALREADY parses each
 * response when it writes pms_inventory — so the useful question is whether
 * that write happened, which is answerable from two timestamps.
 */

function hostOf(url: string): string {
  if (url == null || url === '') {
    return ''
  }

  try {
    return new URL(url).host.toLowerCase()
  } catch {
    // Endpoints are not always absolute URLs; fall back to the first path-free
    // token so a bare host still matches.
    return url.replace(/^\/\//, '').split('/')[0].toLowerCase()
  }
}

/**
 * Comlogs are filterable by property but not by provider — EntityFilters has no
 * provider dimension — while the comparison beside them is scoped to ONE
 * provider. On a property with more than one integration configured, the newest
 * conversation could belong to a different provider entirely, so a healthy
 * integration could be reported as failing on another's log.
 *
 * The endpoint is the discriminator available on both sides.
 */
export function logsForProvider(
  logs: ComLog[],
  providerURL: string | null
): ComLog[] {
  if (providerURL == null || providerURL === '') {
    return logs
  }

  const host = hostOf(providerURL)
  if (host === '') {
    return logs
  }

  const scoped = logs.filter((log) => hostOf(log.EndpointURL) === host)

  // Rather than reporting "no conversation" — which would read as "the PMS was
  // never asked" — fall back to the unscoped set when the endpoint cannot be
  // matched. A slightly wider claim is better than a false negative on the one
  // question this panel answers.
  return scoped.length > 0 ? scoped : logs
}

export type EvidenceVerdict =
  | { kind: 'no-pull' }
  | { kind: 'pull-failed'; log: ComLog }
  | { kind: 'not-landed'; log: ComLog; storedAt: string | null }
  | { kind: 'landed'; log: ComLog; storedAt: string | null }

/** The BFF stores an HTTP status; anything outside 2xx did not return data. */
export function succeeded(log: ComLog): boolean {
  return log.Status >= 200 && log.Status < 300
}

export function latestLog(logs: ComLog[]): ComLog | null {
  const sorted = [...logs].sort(
    (a, b) => moment(b.Date).valueOf() - moment(a.Date).valueOf()
  )

  return sorted[0] ?? null
}

export type ConversationMix = {
  total: number
  failed: number
  /** True when the window holds BOTH successful and failed conversations. */
  mixed: boolean
}

/**
 * How the conversations in this window actually went — all of them, not just
 * the newest.
 *
 * The verdict below is built from `latestLog` alone, and on a multi-channel
 * property that single log does not speak for the rest. Haris makes one GetRap
 * call PER CHANNEL — 15 per cycle on property 10700 — so whichever finished
 * last became "the evidence" for all of them. On that property two channels
 * were returning HTTP 400 (the PMS refusing the channel manager's access) while
 * four returned 200, and the panel showed whichever won the race.
 *
 * Both directions of that are wrong: a newest-200 hides two broken channels, and
 * a newest-400 condemns four working ones.
 *
 * This cannot be resolved into a per-channel verdict here. The ComLog carries
 * the channel as an entity only after the pmsmanager change that adds it, and
 * FILTERING by accommodation AND channel together needs `repeated Entity` on
 * logpb's GetComLogsListRequest, which is singular today (log.proto:110) — a
 * grpc-repo contract change. Until then the honest thing is to report the mix
 * rather than to pick a winner and present it as the whole truth.
 */
export function conversationMix(logs: ComLog[]): ConversationMix {
  const failed = logs.filter((log) => !succeeded(log)).length

  return {
    total: logs.length,
    failed,
    mixed: failed > 0 && failed < logs.length
  }
}

/**
 * The newest moment any PMS row was written.
 *
 * Rows whose PMS layer is absent or carries no change time contribute nothing —
 * a missing timestamp is not evidence of an old one, and treating it as the
 * epoch would report every property as never updated.
 */
export function newestPMSWrite(rows: InventoryComparisonRow[]): string | null {
  let newest: number | null = null
  let value: string | null = null

  rows.forEach((row) => {
    if (!row.pms.present || row.pms.changedAt == null) {
      return
    }
    const at = moment(row.pms.changedAt).valueOf()
    if (newest == null || at > newest) {
      newest = at
      value = row.pms.changedAt
    }
  })

  return value
}

/**
 * Compares the last PMS conversation against the last PMS write.
 *
 * `toleranceSeconds` exists because the write is asynchronous: the response
 * lands, then a worker parses and stores it. Without a tolerance every freshly
 * triggered pull would report as not-landed for the seconds in between, which
 * is the false alarm this screen exists to remove rather than create.
 */
export function assessEvidence(
  logs: ComLog[],
  rows: InventoryComparisonRow[],
  toleranceSeconds = 120
): EvidenceVerdict {
  const log = latestLog(logs)
  if (log == null) {
    return { kind: 'no-pull' }
  }

  if (!succeeded(log)) {
    return { kind: 'pull-failed', log }
  }

  const storedAt = newestPMSWrite(rows)
  if (storedAt == null) {
    // The PMS answered and nothing was stored at all. Reported as not-landed
    // rather than as an absence, because "the PMS talked to us and our
    // inventory is empty" is a pipeline fault, not missing data.
    //
    // storedAt stays NULL. Filling it with the log's own date rendered
    // "inventory last written a few seconds ago" directly beneath a headline
    // saying it was never written — the panel contradicting itself in the one
    // situation it is most important to be believed.
    return { kind: 'not-landed', log, storedAt: null }
  }

  const gap = moment(log.Date).diff(moment(storedAt), 'seconds')

  return gap > toleranceSeconds
    ? { kind: 'not-landed', log, storedAt }
    : { kind: 'landed', log, storedAt }
}
